import React from 'react';
import { useSelector } from 'react-redux';
import { FaCheckCircle, FaMinusCircle } from 'react-icons/fa';
import {
  Button,
  Modal,
  Subheading,
  ModalCards,
  ModalCard,
  SubscriptionModalCard,
  MobileTitle,
  PayUpfrontButton,
  Tooltip,
  DesktopSubscribeButton,
  MobileSubscribeButton
} from './SubscriptionModal.styled';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  switchToSubscription: () => void;
}

const SubscriptionModal = ({
  isOpen,
  onClose,
  switchToSubscription
}: SubscriptionModalProps) => {
  const { contract: cancelledContract } = useSelector(
    (state: any) => state.nomadInsurance.contract.lastCancelledContract
  );

  const isZeroDeductible = cancelledContract?.deductible !== 250;

  return (
    <Modal
      isOpen={isOpen}
      // does nothing because close button is hidden with css
      onClose={() => {}}
      isScrollable
      heading="Paying as you go might suit your travels better"
    >
      <Subheading>
        For the same price, there are built-in pros of auto-extending your policy every 4
        weeks compared to paying for multiple policies within the same time period:
      </Subheading>

      <MobileTitle>Benefits of paying as you go</MobileTitle>

      <ModalCards>
        <ModalCard>
          <h3>Pay upfront</h3>
          {isZeroDeductible ? (
            <ul>
              <li>
                <FaMinusCircle />
                Any medical issues that come up during this policy, even undiagnosed, will
                not be covered in your next policy
              </li>
              <li>
                <FaMinusCircle />
                Must pay for 90+ days upfront to get 30 days of home country coverage
              </li>
              <li>
                <FaMinusCircle />
                No access to $20 credits for referring friends and family to Nomad
                Insurance"
              </li>
            </ul>
          ) : (
            <ul>
              <li>
                <FaMinusCircle />
                The US$250 deductible resets for each new policy within a 364-day period
              </li>
              <li>
                <FaMinusCircle />
                Must pay for 90+ days upfront to get 30 days of home country coverage
              </li>
              <li>
                <FaMinusCircle />
                Any medical issues that come up during this policy, even undiagnosed, will
                not be covered in your next policy
              </li>
            </ul>
          )}
          <p>To cancel early, you will need to speak with customer care.</p>
          <Button onClick={onClose} color="fuzzyWuzzyBrown">
            I'll pay upfront
          </Button>
        </ModalCard>
        <SubscriptionModalCard>
          <h3>Pay as you go</h3>
          {isZeroDeductible ? (
            <ul>
              <li>
                <FaCheckCircle />
                Add extra coverage for adventure sports and electronic theft
              </li>
              <li>
                <FaCheckCircle />
                <div>
                  After 364 days of continuous coverage, your next policy will cover{' '}
                  <Tooltip variant="underline" text="undiagnosed illness">
                    Except for illness or injury that is untreatable and expected to
                    become worse over time or is a general exclusion in the policy
                  </Tooltip>{' '}
                  that began during that period
                </div>
              </li>
              <li>
                <FaCheckCircle />
                Get 30 days of home country coverage every 90 days abroad
              </li>
            </ul>
          ) : (
            <ul>
              <li>
                <FaCheckCircle />
                The US$250 deductible will apply only once in 364 days, instead of for
                each new policy within that period
              </li>
              <li>
                <FaCheckCircle />
                Get 30 days of home country coverage every 90 days abroad
              </li>
              <li>
                <FaCheckCircle />
                <div>
                  After 364 days of continuous coverage, your next policy will cover{' '}
                  <Tooltip variant="underline" text="undiagnosed illness">
                    Except for illness or injury that is untreatable and expected to
                    become worse over time or is a general exclusion in the policy
                  </Tooltip>{' '}
                  that began during that period
                </div>
              </li>
            </ul>
          )}

          <p>
            Remember, you can <b>easily stop</b> your insurance through your dashboard.
          </p>
          <DesktopSubscribeButton onClick={switchToSubscription} color="fountainBlue">
            I'll pay as I go
          </DesktopSubscribeButton>
          <MobileSubscribeButton onClick={switchToSubscription} color="pickledBluewood">
            I'll pay as I go
          </MobileSubscribeButton>
        </SubscriptionModalCard>
      </ModalCards>
      <PayUpfrontButton onClick={onClose} color="fuzzyWuzzyBrown">
        I'll pay upfront
      </PayUpfrontButton>
    </Modal>
  );
};

export { SubscriptionModal };
